import Quill from 'quill';
import { DOCUMENT_FONT_FAMILY_OPTIONS } from 'src/app/modules/document/pages/edit/constants/document-font-select-options.generated';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Parchment = Quill.import('parchment') as any;

/**
 * Tamanho e fonte POR TRECHO do texto (formatos `size` e `font` do Delta), aplicados pela
 * barra de formatação ao que está selecionado. Ver docs/PLANO-EDICAO-INLINE.md.
 *
 * Contrato do Delta (o PdfEngine e o pdf-renderer leem exatamente isto):
 * - `size`: número em PONTOS como string ("14", "10.5"), de 6 a 72 em meio ponto;
 * - `font`: uma família da lista do documento (DOCUMENT_FONT_FAMILY_OPTIONS).
 *
 * No DOM do editor os dois vão num atributo de dados (é dele que o Quill lê o valor de
 * volta) e numa variável CSS no style, que o CSS do card transforma em font-size
 * (12pt = 1rem do editor) e font-family. Variável, e não `font-size`/`font-family` direto,
 * porque o clipboard do Quill casa esses dois com os atributores padrão dele (font-size em
 * px, font-family serif/monospace) e descartava o nosso valor ao reler o DOM.
 *
 * Substituem os `size` (small/large/huge) e `font` (serif/monospace) que o Quill registra
 * por padrão, que nunca foram usados aqui.
 */
export const SPAN_SIZE_ATTR = 'data-span-pt';
export const SPAN_FONT_ATTR = 'data-span-font';

const MIN_SPAN_PT = 6;
const MAX_SPAN_PT = 72;

/** "14", "10.5" ou 14 => "14" / "10.5"; fora da faixa ou do passo de meio ponto => null. */
export function normalizeSpanPt(raw: unknown): string | null {
    const value = typeof raw === 'number' ? raw : Number(String(raw ?? '').trim().replace(',', '.'));
    if (!Number.isFinite(value) || value < MIN_SPAN_PT || value > MAX_SPAN_PT) return null;
    if (Math.round(value * 2) !== value * 2) return null;
    return String(value);
}

export function isSpanFont(raw: unknown): raw is string {
    return typeof raw === 'string' && DOCUMENT_FONT_FAMILY_OPTIONS.includes(raw);
}

class SpanSizeAttributor extends Parchment.Attributor {
    constructor() {
        super('size', SPAN_SIZE_ATTR, { scope: Parchment.Scope.INLINE });
    }

    add(node: HTMLElement, value: unknown): boolean {
        const pt = normalizeSpanPt(value);
        if (pt === null || !super.add(node, pt)) return false;
        node.style.setProperty('--span-pt', pt);
        return true;
    }

    remove(node: HTMLElement): void {
        super.remove(node);
        node.style.removeProperty('--span-pt');
        if (!node.getAttribute('style')) node.removeAttribute('style');
    }
}

class SpanFontAttributor extends Parchment.Attributor {
    constructor() {
        super('font', SPAN_FONT_ATTR, { scope: Parchment.Scope.INLINE });
    }

    add(node: HTMLElement, value: unknown): boolean {
        if (!isSpanFont(value) || !super.add(node, value)) return false;
        node.style.setProperty('--span-font', value);
        return true;
    }

    remove(node: HTMLElement): void {
        super.remove(node);
        node.style.removeProperty('--span-font');
        if (!node.getAttribute('style')) node.removeAttribute('style');
    }
}

let registered = false;

/** Idempotente: o editor e o viewer chamam ao nascer, e o primeiro que chega registra. */
export function registerSpanStyleFormats(): void {
    if (registered) return;
    registered = true;
    Quill.register({ 'formats/size': new SpanSizeAttributor(), 'formats/font': new SpanFontAttributor() }, true);
}
